import { useMemo, useState } from 'react';
import { LuChevronLeft, LuChevronRight, LuGrid3X3, LuLoader, LuSearch } from 'react-icons/lu';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Person {
    id: string;
    name: string;
    email: string;
    household: string;
    keywords: string[];
}

interface SegmentationKeyword {
    id: string;
    keyword: string;
}

interface KeywordBulkEditorProps {
    persons: Person[];
    keywords: SegmentationKeyword[];
    onToggleAssociation: (personId: string, keyword: string) => void;
    isLoading?: boolean;
    loadingAssociations?: Set<string>;
}

export function KeywordBulkEditor({
    persons,
    keywords,
    onToggleAssociation,
    isLoading = false,
    loadingAssociations = new Set(),
}: KeywordBulkEditorProps) {
    const [personFilter, setPersonFilter] = useState('');
    const [keywordFilter, setKeywordFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const filteredPersons = useMemo(
        () =>
            persons.filter(
                (p) =>
                    p.name.toLowerCase().includes(personFilter.toLowerCase()) ||
                    p.email.toLowerCase().includes(personFilter.toLowerCase()) ||
                    p.household.toLowerCase().includes(personFilter.toLowerCase()),
            ),
        [persons, personFilter],
    );

    const filteredKeywords = useMemo(
        () => keywords.filter((k) => k.keyword.toLowerCase().includes(keywordFilter.toLowerCase())),
        [keywords, keywordFilter],
    );

    // Pagination calculations
    const totalPersons = filteredPersons.length;
    const totalPages = Math.ceil(totalPersons / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalPersons);
    const paginatedPersons = filteredPersons.slice(startIndex, endIndex);

    // Reset to page 1 when filters change
    const handlePersonFilterChange = (value: string) => {
        setPersonFilter(value);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (value: string) => {
        setPageSize(Number(value));
        setCurrentPage(1);
    };

    const getInitials = (name: string) =>
        name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase();

    return (
        <Card className="max-w-full min-w-0 overflow-hidden">
            <CardHeader className="py-4">
                <CardTitle className="flex items-center gap-2 text-base font-medium">
                    <LuGrid3X3 className="h-4 w-4" />
                    Bulk Edit Associations
                </CardTitle>
            </CardHeader>
            <CardContent className="min-w-0">
                <div className="mb-4 flex flex-col gap-3 md:flex-row">
                    <div className="relative flex-1 md:max-w-xs">
                        <LuSearch className="text-muted-foreground absolute top-2.5 left-2.5 h-3.5 w-3.5" />
                        <Input
                            placeholder="Filter people..."
                            value={personFilter}
                            onChange={(e) => handlePersonFilterChange(e.target.value)}
                            className="h-9 pl-8 text-sm"
                        />
                    </div>
                    <div className="relative flex-1 md:max-w-xs">
                        <LuSearch className="text-muted-foreground absolute top-2.5 left-2.5 h-3.5 w-3.5" />
                        <Input
                            placeholder="Filter keywords..."
                            value={keywordFilter}
                            onChange={(e) => setKeywordFilter(e.target.value)}
                            className="h-9 pl-8 text-sm"
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex h-64 items-center justify-center">
                        <div className="flex flex-col items-center gap-2">
                            <LuLoader className="text-primary h-8 w-8 animate-spin" />
                            <p className="text-muted-foreground text-sm">Loading people and associations...</p>
                        </div>
                    </div>
                ) : (
                    <div className="max-w-full min-w-0 overflow-hidden">
                        <ScrollArea className="w-full max-w-full">
                            <div className="min-w-max">
                                {/* Header row */}
                                <div className="flex border-b">
                                    <div className="text-muted-foreground bg-background sticky left-0 z-10 w-52 shrink-0 p-2 text-xs font-medium">
                                        Person
                                    </div>
                                    {filteredKeywords.map((kw) => (
                                        <div key={kw.id} className="w-28 shrink-0 p-2 text-center">
                                            <Badge
                                                variant="outline"
                                                className="block max-w-full truncate px-1.5 text-[10px] leading-tight font-normal"
                                                title={kw.keyword}
                                            >
                                                {kw.keyword}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>

                                {/* Person rows */}
                                {paginatedPersons.map((person) => (
                                    <div
                                        key={person.id}
                                        className="hover:bg-muted/30 flex items-center border-b transition-colors last:border-b-0"
                                    >
                                        <div className="bg-background sticky left-0 z-10 flex w-52 shrink-0 items-center gap-2 p-2">
                                            <Avatar className="h-7 w-7">
                                                <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                                                    {getInitials(person.name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="min-w-0">
                                                <p className="truncate text-sm leading-tight font-medium">
                                                    {person.name}
                                                </p>
                                                <p className="text-muted-foreground truncate text-[11px] leading-tight">
                                                    {person.household}
                                                </p>
                                            </div>
                                        </div>
                                        {filteredKeywords.map((kw) => {
                                            const isAssociated = person.keywords.includes(kw.keyword);
                                            const loadingKey = `${person.id}-${kw.keyword}`;
                                            const isCheckboxLoading = loadingAssociations.has(loadingKey);

                                            return (
                                                <div key={kw.id} className="flex w-28 shrink-0 justify-center p-2">
                                                    {isCheckboxLoading ? (
                                                        <LuLoader className="text-primary h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Checkbox
                                                            checked={isAssociated}
                                                            onCheckedChange={() =>
                                                                onToggleAssociation(person.id, kw.keyword)
                                                            }
                                                            className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}

                                {totalPersons === 0 && (
                                    <div className="text-muted-foreground py-8 text-center text-sm">
                                        No people match the filter
                                    </div>
                                )}
                            </div>
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </div>
                )}

                {/* Pagination Controls */}
                {!isLoading && totalPersons > 0 && (
                    <div className="mt-4 flex items-center justify-between border-t pt-4">
                        <div className="flex items-center gap-4">
                            <p className="text-muted-foreground text-sm">
                                Showing {startIndex + 1}-{endIndex} of {totalPersons} people
                            </p>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">Rows per page:</span>
                                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                                    <SelectTrigger className="h-8 w-20">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">10</SelectItem>
                                        <SelectItem value="25">25</SelectItem>
                                        <SelectItem value="50">50</SelectItem>
                                        <SelectItem value="100">100</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground text-sm">
                                Page {currentPage} of {totalPages}
                            </span>
                            <div className="flex gap-1">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <LuChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    <LuChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
